import { useInfiniteQuery } from "@tanstack/react-query";
import { makeRequest } from "../makeRequest";

type ApiResponse<T = any> = {
  success?: boolean;
  data: T[];
  nextCursor?: string | number | null;
  hasMore?: boolean;
  total?: number;
  totalCount?: number;
  count?: number;
  page?: number;
  items?: T[];
  results?: T[];
};

type FetchListType = {
  endpoint: string;
  method?: string;
  key: any[];
  query?: Record<string, any>;
  paginationType?: "cursor" | "page";
  pageParamName?: string;
  cursorParamName?: string;
  pageSize?: number;
  enabled?: boolean;
};

export function myGetList<T = any>({
  endpoint,
  method = "GET",
  key,
  query = {},
  paginationType = "cursor",
  pageParamName = "page",
  cursorParamName = "cursor",
  pageSize = 20,
  enabled = true,
}: FetchListType) {
  return useInfiniteQuery({
    queryKey: [...key, paginationType, pageSize, pageParamName, cursorParamName],
    
    queryFn: async ({ pageParam = null }) => {
      let requestQuery = { ...query };
      
      // Add pageSize/limit to query if provided
      if (pageSize) {
        requestQuery = { ...requestQuery, limit: pageSize };
      }
      
      // Add pagination parameter
      if (pageParam !== null && pageParam !== undefined) {
        if (paginationType === "page") {
          requestQuery = { ...requestQuery, [pageParamName]: pageParam };
        } else {
          // Cursor-based pagination - send cursor
          requestQuery = { ...requestQuery, [cursorParamName]: pageParam };
        }
      }
      
      const res = await makeRequest<ApiResponse<T>>(endpoint, {
        method,
        query: requestQuery,
        auth: true,
      });

      return res;
    },

    getNextPageParam: (lastPage, allPages) => {
      if (paginationType === "page") {
        // Page-based pagination logic
        const currentPageData = lastPage.data || lastPage.items || lastPage.results || [];
        const total = lastPage.total || lastPage.totalCount || lastPage.count;
        
        if (total !== undefined && total !== null) {
          const loadedItems = allPages.reduce((acc, page) => {
            const pageData = page.data || page.items || page.results || [];
            return acc + pageData.length;
          }, 0);
          return loadedItems < total ? allPages.length + 1 : undefined;
        }
        
        return currentPageData.length < pageSize ? undefined : allPages.length + 1;
      }
      
      // Cursor-based pagination - YOUR API FORMAT
      // Your API returns: { success, data, nextCursor, hasMore }
      const hasMore = lastPage.hasMore === true;
      const nextCursor = lastPage.nextCursor;
      
      return hasMore && nextCursor !== undefined && nextCursor !== null 
        ? nextCursor 
        : undefined;
    },

    initialPageParam: null,
    staleTime: 1000 * 60 * 2,
    enabled,
  });
}